import { PlanLimits } from './planConfig';

export type UsageResource = 'users' | 'projects' | 'clients';

export interface ResourceUsage {
  resource: UsageResource;
  current: number;
  limit: number;
  label: string;
}

const RESOURCE_LABELS: Record<UsageResource, string> = {
  users: 'team members',
  projects: 'projects',
  clients: 'clients',
};

export interface PlanUsageCounts {
  users: number;
  projects: number;
  clients: number;
}

function isFiniteLimit(limit: number): limit is number {
  return Number.isFinite(limit);
}

export function isAtLimit(
  limits: PlanLimits,
  usage: PlanUsageCounts,
  resource: UsageResource
): boolean {
  const limitMap = {
    users: limits.maxUsers,
    projects: limits.maxProjects,
    clients: limits.maxClients,
  };
  const limit = limitMap[resource];
  return isFiniteLimit(limit) && usage[resource] >= limit;
}

export function getResourceLimitMessage(
  limits: PlanLimits,
  usage: PlanUsageCounts,
  resource: UsageResource
): string {
  const limitMap = {
    users: limits.maxUsers,
    projects: limits.maxProjects,
    clients: limits.maxClients,
  };
  const limit = limitMap[resource];
  const label = RESOURCE_LABELS[resource];
  return `You've reached your plan limit for ${label} (${usage[resource]}/${limit}). Upgrade to add more.`;
}

export function getExceededLimits(
  limits: PlanLimits,
  usage: PlanUsageCounts
): ResourceUsage[] {
  const checks: Array<{ resource: UsageResource; current: number; limit: number }> = [
    { resource: 'users', current: usage.users, limit: limits.maxUsers },
    { resource: 'projects', current: usage.projects, limit: limits.maxProjects },
    { resource: 'clients', current: usage.clients, limit: limits.maxClients },
  ];

  return checks
    .filter(({ current, limit }) => isFiniteLimit(limit) && current >= limit)
    .map(({ resource, current, limit }) => ({
      resource,
      current,
      limit,
      label: RESOURCE_LABELS[resource],
    }));
}

export function formatExceededLimitsMessage(
  exceeded: ResourceUsage[],
  audience: 'admin' | 'member' = 'admin'
): string {
  if (exceeded.length === 0) return '';

  const upgradeHint =
    audience === 'admin'
      ? 'Upgrade to add more.'
      : 'Ask your workspace admin to upgrade the plan.';

  const multiUpgradeHint =
    audience === 'admin'
      ? 'Upgrade to continue growing.'
      : 'Ask your workspace admin to upgrade the plan.';

  const prefix = audience === 'admin' ? "You've" : 'This workspace has';

  if (exceeded.length === 1) {
    const { label, current, limit } = exceeded[0];
    return `${prefix} reached ${audience === 'admin' ? 'your' : 'its'} plan limit for ${label} (${current}/${limit}). ${upgradeHint}`;
  }

  const parts = exceeded.map(({ label, current, limit }) => `${label} (${current}/${limit})`);
  return `${prefix} reached ${audience === 'admin' ? 'your' : 'its'} plan limits for ${parts.join(', ')}. ${multiUpgradeHint}`;
}
